// src/components/FaqList.tsx
'use client';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Container, Paper, styled, Typography, useTheme } from '@mui/material';
import React from 'react';
type Faq = {
  questionName: string;
  acceptedAnswerText: string;
};
type FaqListProps = {
  faqs: Faq[];
};
const AccentDivider = styled('div')<{
  expanded: boolean;
}>(({
  theme,
  expanded
}) => ({
  width: 4,
  backgroundColor: theme.palette.primary.main,
  borderTopLeftRadius: expanded ? 4 : 0,
  borderBottomLeftRadius: expanded ? 4 : 0,
  transition: 'height 0.3s',
  height: expanded ? 56 : 56
}));
export default function FaqList({
  faqs
}: FaqListProps) {
  const theme = useTheme();
  const [expandedIdx, setExpandedIdx] = React.useState<number | null>(null);
  return <Box component="section" sx={{
    bgcolor: 'background.default',
    py: {
      xs: 2
    }
  }} data-sentry-element="Box" data-sentry-component="FaqList" data-sentry-source-file="FAQsSection.tsx">
      <Container maxWidth="md" data-sentry-element="Container" data-sentry-source-file="FAQsSection.tsx">
        <Typography variant="h3" component="h2" fontWeight={800} gutterBottom textAlign="center" sx={{
        mb: {
          xs: 4,
          md: 6
        }
      }} data-sentry-element="Typography" data-sentry-source-file="FAQsSection.tsx">
          Frequently Asked Questions
        </Typography>

        {faqs.map((faq, idx) => {
        const isExpanded = expandedIdx === idx;
        return <Paper key={idx} elevation={isExpanded ? 6 : 1} sx={{
          display: 'flex',
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'box-shadow 0.3s'
        }}>
              <AccentDivider expanded={isExpanded} />

              <Accordion expanded={isExpanded} onChange={() => setExpandedIdx(isExpanded ? null : idx)} elevation={0} sx={{
            'flex': 1,
            'bgcolor': 'transparent',
            'border': 'none',
            'boxShadow': 'none',
            '&:before': {
              display: 'none'
            }
          }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{
              color: theme.palette.primary.main,
              transition: 'transform 0.3s',
              ...(isExpanded && {
                transform: 'rotate(180deg)'
              })
            }} />} aria-controls={`faq-content-${idx}`} id={`faq-header-${idx}`} sx={{
              'px': 3,
              'py': 2,
              'minHeight': 64,
              '& .MuiAccordionSummary-content': {
                margin: 0
              },
              'borderBottom': isExpanded ? 'none' : `1px solid ${theme.palette.divider}`
            }}>
                  <Typography variant="h6" fontWeight={600} component="div">
                    {faq.questionName}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{
              px: 3,
              py: 2,
              pt: 0
            }}>
                  <Typography variant="body1" color="text.secondary" sx={{
                lineHeight: 1.7
              }}>
                    {faq.acceptedAnswerText}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Paper>;
      })}
      </Container>
    </Box>;
}